import { Alert, Button, Group, Stack, Text, Modal, Badge } from '@mantine/core';
import { IconAlertTriangle, IconCreditCard, IconCrown } from '@tabler/icons-react';
import { useSubscriptionLimits } from '@/hooks/useSubscriptionLimits';

interface SubscriptionLimitAlertProps {
  show?: boolean;
  variant?: 'banner' | 'modal';
}

export function SubscriptionLimitAlert({
  show = true,
  variant = 'banner',
}: SubscriptionLimitAlertProps) {
  const {
    isLoading,
    isTrialMode,
    isTrialExpired,
    isAtAssetLimit,
    isOverAssetLimit,
    assetLimit,
    currentAssetCount,
    assetsRemaining,
    daysUntilExpiry,
    goToBilling,
    limitMessage,
  } = useSubscriptionLimits();

  if (isLoading || !show) return null;

  // Determine severity
  const isCritical = isTrialExpired || isOverAssetLimit;
  const isWarning = isAtAssetLimit || (isTrialMode && daysUntilExpiry !== null && daysUntilExpiry <= 7);

  if (!isCritical && !isWarning) return null;

  const alertColor = isCritical ? 'red' : 'yellow';
  const alertIcon = isCritical ? <IconAlertTriangle size={20} /> : <IconCrown size={20} />;

  const title = isTrialExpired
    ? 'Trial Expired'
    : isOverAssetLimit
    ? 'Asset Limit Exceeded'
    : isAtAssetLimit
    ? 'Asset Limit Reached'
    : 'Trial Ending Soon';

  if (variant === 'banner') {
    return (
      <Alert
        icon={alertIcon}
        title={title}
        color={alertColor}
        variant="filled"
        mb="md"
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Stack gap={4}>
            <Text size="sm">{limitMessage}</Text>
            {assetLimit !== null && (
              <Group gap="xs">
                <Badge size="sm" color="white" variant="outline">
                  {currentAssetCount} / {assetLimit} assets
                </Badge>
                {assetsRemaining !== null && assetsRemaining > 0 && (
                  <Badge size="sm" color="white" variant="outline">
                    {assetsRemaining} remaining
                  </Badge>
                )}
              </Group>
            )}
          </Stack>
          <Button
            variant="white"
            color={alertColor}
            size="sm"
            leftSection={<IconCreditCard size={16} />}
            onClick={goToBilling}
          >
            {isTrialExpired || isOverAssetLimit ? 'Upgrade Now' : 'Manage Billing'}
          </Button>
        </Group>
      </Alert>
    );
  }

  return null;
}

interface LimitBlockModalProps {
  opened: boolean;
  onClose: () => void;
  action?: string;
}

export function LimitBlockModal({ opened, onClose, action = 'perform this action' }: LimitBlockModalProps) {
  const {
    isTrialExpired,
    isAtAssetLimit,
    isOverAssetLimit,
    assetLimit,
    currentAssetCount,
    limitMessage,
    goToBilling,
  } = useSubscriptionLimits();

  const title = isTrialExpired
    ? 'Trial Expired'
    : isOverAssetLimit || isAtAssetLimit
    ? 'Asset Limit Reached'
    : 'Subscription Required';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconAlertTriangle size={24} color="var(--mantine-color-red-6)" />
          <Text fw={600}>{title}</Text>
        </Group>
      }
      centered
    >
      <Stack gap="md">
        <Text>{limitMessage || `You need to upgrade your subscription to ${action}.`}</Text>

        {assetLimit !== null && (
          <Alert color="gray" variant="light">
            <Group justify="space-between">
              <Text size="sm">Current usage:</Text>
              <Badge size="lg" color={isOverAssetLimit ? 'red' : isAtAssetLimit ? 'yellow' : 'blue'}>
                {currentAssetCount} / {assetLimit} assets
              </Badge>
            </Group>
          </Alert>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="blue"
            leftSection={<IconCreditCard size={16} />}
            onClick={() => {
              goToBilling();
              onClose();
            }}
          >
            Go to Billing
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
